'use client';

import { useGSAP } from '@gsap/react';
import clsx from 'clsx';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { useState } from 'react';

import { ButtonLink } from '@commons/components/Button';
import DrupalImage from '@commons/components/Image/Drupal';
import RichText from '@commons/components/RichText';
import Texture from '@commons/components/Texture';
import BaseContainer from '@commons/containers/BaseContainer';
import Flex from '@commons/containers/Flex';
import { usePageContext } from '@commons/contexts/page';
import PageBuilder from '@commons/pagebuilder';
import WebformDrawer from '@commons/pagebuilder/webforms/Drawer';

import type { ScrollableSlideProps } from '.';
import styles from './ScrollableSlide.module.scss';

// GSAP Plugins registration.

gsap.registerPlugin(ScrollTrigger);

/**
 * Scrollable Slide block component.
 *
 * @see https://gsap.com/resources/React/
 * @see https://gsap.com/docs/v3/Plugins/ScrollTrigger/
 */

const ScrollableSlideBlock = ({ content }: ScrollableSlideProps) => {
  const [active, setActive] = useState(false);
  const { isEditing } = usePageContext();

  const { body, backgroundImage, link, id, linkType = 'default', linkText } = content;

  /**
   * This will pin the block when it reaches the top of the viewport.
   * On scroll, it will slide the scrollable text upwards.
   */
  useGSAP(
    () => {
      // Early return if the editor is active.
      if (isEditing) return;

      const animatedTexture = document.querySelector('.animated-background') as HTMLElement;
      if (animatedTexture) {
        // Initial setup for the text element to be below the container

        gsap.to(animatedTexture, {
          y: `-100%`,
          ease: 'none',
          scrollTrigger: {
            trigger: '#sectionPin',
            pin: true,
            scrub: true,
            start: 'top top',
            anticipatePin: 1,
            end: '+=500',
          },
        });

        // Slide-up animation for the text moving opposite to the scroll direction
        gsap.to(animatedTexture, {
          y: 0,
          opacity: 1,
          ease: 'power2.out',
          scrollTrigger: {
            start: 'center center',
            end: 'top+=16 top',
            scrub: true,
          },
        });
      }
    },
    { dependencies: [isEditing] }
  );

  const renderButton = () => {
    if (linkType === 'webform' && id && linkText) {
      const renderedWebform = PageBuilder.renderWebform(id, { inDrawer: true });
      return (
        <WebformDrawer webformId={id} triggerProps={{ label: linkText }}>
          {renderedWebform}
        </WebformDrawer>
      );
    }

    if (linkText && link?.url) {
      return (
        <ButtonLink xl={true} href={link?.url || '#'} variant="secondary">
          {linkText}
        </ButtonLink>
      );
    }
  };

  return (
    <BaseContainer width={12} id="sectionPin" className={styles.wrapper}>
      <DrupalImage
        {...backgroundImage}
        className={clsx(styles.backgroundImage, 'h-100', 'w-100')}
      />
      <Flex
        width={12}
        padding={{ xs: 'xxs', lg: 'xxl' }}
        direction="column"
        theme="black"
        align="center"
        justify="space-evenly"
        className={clsx(
          styles.pinWrap,
          { [styles.active]: active },
          'animated-background',
          'h-100',
          'w-100'
        )}
      >
        <Texture animated className={styles.texture} />
        <RichText value={body} className={styles.content} />
        <BaseContainer onMouseEnter={() => setActive(true)} onMouseLeave={() => setActive(false)}>
          {renderButton()}
        </BaseContainer>
      </Flex>
    </BaseContainer>
  );
};

export default ScrollableSlideBlock;
